declare const __API_ORIGIN__: string;
declare const __GOOGLE_CLIENT_ID__: string;
declare const __IS_PRODUCTION__: boolean;

export const apiOrigin = __API_ORIGIN__;
export const googleClientId = __GOOGLE_CLIENT_ID__;
export const isProductionBuild = __IS_PRODUCTION__;
const authStorageKey = "iot-monitoring.authToken";

function apiUrl(path: string) {
  if (!apiOrigin) {
    return path;
  }

  return `${apiOrigin}${path}`;
}

export function getAuthToken() {
  return window.localStorage.getItem(authStorageKey);
}

export function setAuthToken(token: string | null) {
  if (token) {
    window.localStorage.setItem(authStorageKey, token);
    return;
  }

  window.localStorage.removeItem(authStorageKey);
}

export type HealthResponse = {
  ok: boolean;
  service: string;
  environment: string;
  uptimeSeconds: number;
  startedAt: string;
  integrations: {
    timescale: boolean;
    localDb?: boolean;
    redis: boolean;
    localCache?: boolean;
    mqtt: boolean;
    socketio: boolean;
  };
};

export type PlatformMetrics = {
  activeMqttConnections: number;
  mqttConnectionsTotal: number;
  messagesReceivedTotal: number;
  messagesDroppedTotal: number;
  errorsTotal: number;
  lastMessageAt: string | null;
};

export type Tenant = {
  id: string;
  name: string;
  status: string;
  tier: string;
  ingestionLimitPerMinute: number;
};

export type UserPreferences = {
  mode: "dark" | "light" | "system";
  density: "comfortable" | "compact";
  defaultDashboard: "platform" | "live" | "admin";
  telemetryRefreshSeconds: number;
  realtimeNotice: boolean;
};

export type AuthUser = {
  id: string;
  tenantId: string;
  provider: "google" | "local";
  email: string;
  name: string;
  avatarUrl?: string;
  role: "tenant-admin" | "global-admin";
  preferences: UserPreferences;
  createdAt: string;
  lastLoginAt: string;
};

export type AuthSession = {
  token: string;
  expiresInSeconds: number;
  user: AuthUser;
  tenant: Tenant;
};

export type NameAvailability = {
  value: string;
  available: boolean;
  reason: string;
};

export type ProfileNameAvailability = {
  userName: NameAvailability;
  tenantName: NameAvailability;
};

export type AdminLocation = {
  id: string;
  tenantId: string;
  building: string;
  floor: string;
  room: string;
};

export type LocationNode = {
  id: string;
  tenantId: string;
  building: string;
  floor: string;
  room: string;
};

export type Feed = {
  key: string;
  label: string;
  unit?: string;
  kind: "line" | "bar" | "gauge";
  tenantId: string;
};

export type TelemetryPoint = {
  tenantId: string;
  locationId: string;
  deviceId: string;
  hardwareId: string;
  feedKey: string;
  valueNumeric: number | null;
  valueText: string | null;
  metadata: Record<string, unknown>;
  time: string;
  source: "mqtt" | "http";
};

export type Device = {
  id: string;
  tenantId: string;
  locationId: string;
  hardwareId: string;
  mqttClientId: string;
  mqttUsername: string;
  accessKeyHash: string;
  status: "active" | "suspended";
};

export type DashboardSummary = {
  tenant: Tenant;
  locations: LocationNode[];
  feeds: Feed[];
  metrics: PlatformMetrics;
  recentTelemetry: TelemetryPoint[];
  generatedAt: string;
};

export type AdminTelemetry = {
  metrics: PlatformMetrics;
  throttling: {
    defaultLocationLimitPerMinute: number;
    flushIntervalMs: number;
    flushBatchSize: number;
  };
};

function buildRequestHeaders(headers?: HeadersInit) {
  const result = new Headers(headers);
  if (!result.has("Content-Type")) {
    result.set("Content-Type", "application/json");
  }
  const token = getAuthToken();
  if (token && !result.has("Authorization")) {
    result.set("Authorization", `Bearer ${token}`);
  }
  return result;
}

async function apiFetch(url: string, options?: RequestInit) {
  return fetch(apiUrl(url), {
    ...options,
    headers: buildRequestHeaders(options?.headers)
  });
}

async function requestJson<T>(url: string, options?: RequestInit): Promise<T> {
  const method = (options?.method ?? "GET").toUpperCase();
  const response = await apiFetch(url, options);

  if (!response.ok) {
    let details = "";
    try {
      const json = (await response.clone().json()) as { error?: string; message?: string };
      const message = json?.error ?? json?.message;
      if (typeof message === "string" && message.length > 0) {
        details = `: ${message}`;
      }
    } catch {
      const text = (await response.text()).trim();
      if (text && !text.startsWith("<")) {
        details = `: ${text.slice(0, 180)}`;
      }
    }

    throw new Error(`${method} ${apiUrl(url)} failed with status ${response.status}${details}`);
  }

  return response.json() as Promise<T>;
}

export async function signInWithGoogle(credential: string) {
  const session = await requestJson<AuthSession>("/api/auth/google", {
    method: "POST",
    body: JSON.stringify({ credential })
  });
  setAuthToken(session.token);
  return session;
}

export async function signInForLocalDevelopment() {
  const session = await requestJson<AuthSession>("/api/auth/dev", {
    method: "POST"
  });
  setAuthToken(session.token);
  return session;
}

export async function getCurrentSession(signal?: AbortSignal) {
  const token = getAuthToken();
  if (!token) {
    return null;
  }

  try {
    const response = await requestJson<Omit<AuthSession, "token" | "expiresInSeconds">>(
      "/api/auth/me",
      { signal }
    );
    return {
      token,
      expiresInSeconds: 0,
      ...response
    };
  } catch (error: unknown) {
    if (!(error instanceof DOMException && error.name === "AbortError")) {
      setAuthToken(null);
    }
    throw error;
  }
}

export async function updateUserPreferences(preferences: Partial<UserPreferences>) {
  return requestJson<{ user: AuthUser }>("/api/users/me/preferences", {
    method: "PUT",
    body: JSON.stringify(preferences)
  });
}

export async function getProfileNameAvailability(input: {
  userName: string;
  tenantName: string;
}) {
  const params = new URLSearchParams({
    userName: input.userName,
    tenantName: input.tenantName
  });
  return requestJson<ProfileNameAvailability>(
    `/api/users/me/name-availability?${params.toString()}`
  );
}

export async function updateUserProfile(input: { name: string; tenantName: string }) {
  return requestJson<{ user: AuthUser; tenant: Tenant }>("/api/users/me/profile", {
    method: "PUT",
    body: JSON.stringify(input)
  });
}

export async function signOut() {
  try {
    await apiFetch("/api/auth/logout", { method: "POST" });
  } finally {
    setAuthToken(null);
  }
}

export async function getHealth(signal?: AbortSignal): Promise<HealthResponse> {
  return requestJson<HealthResponse>("/api/health", { signal });
}

export async function getDashboardSummary(signal?: AbortSignal) {
  return requestJson<DashboardSummary>("/api/dashboard/summary", { signal });
}

export async function getFeeds(signal?: AbortSignal) {
  return requestJson<{ feeds: Feed[] }>("/api/feeds", { signal });
}

export async function getAdminTenants(signal?: AbortSignal) {
  return requestJson<{ tenants: Tenant[] }>("/api/admin/tenants", { signal });
}

export async function getAdminLocations(signal?: AbortSignal) {
  return requestJson<{ locations: AdminLocation[] }>("/api/admin/locations", { signal });
}

export type LocationMutationInput = {
  building: string;
  floor: string;
  room: string;
  tenantId: string;
};

export async function createLocation(input: LocationMutationInput) {
  return requestJson<{ location: AdminLocation }>("/api/admin/locations", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export async function updateLocation(
  locationId: string,
  input: Partial<LocationMutationInput>
) {
  return requestJson<{ location: AdminLocation }>(
    `/api/admin/locations/${encodeURIComponent(locationId)}`,
    {
      method: "PUT",
      body: JSON.stringify(input)
    }
  );
}

export async function deleteLocation(locationId: string) {
  const response = await apiFetch(`/api/admin/locations/${encodeURIComponent(locationId)}`, {
    method: "DELETE"
  });

  if (!response.ok) {
    throw new Error(`Delete location failed with status ${response.status}`);
  }
}

export async function getAdminTelemetry(signal?: AbortSignal) {
  return requestJson<AdminTelemetry>("/api/admin/telemetry", { signal });
}

export async function getDevices(signal?: AbortSignal) {
  return requestJson<{ devices: Device[] }>("/api/devices", { signal });
}

export async function updateDeviceLocation(deviceId: string, locationId: string) {
  return requestJson<{ device: Device }>(`/api/devices/${encodeURIComponent(deviceId)}`, {
    method: "PUT",
    body: JSON.stringify({ locationId })
  });
}

export async function createFeed(feed: Feed) {
  return requestJson<{ feed: Feed }>("/api/feeds", {
    method: "POST",
    body: JSON.stringify(feed)
  });
}

export async function updateFeed(feedKey: string, feed: Feed) {
  return requestJson<{ feed: Feed }>(`/api/feeds/${encodeURIComponent(feedKey)}`, {
    method: "PUT",
    body: JSON.stringify(feed)
  });
}

export async function deleteFeed(feedKey: string) {
  const response = await apiFetch(`/api/feeds/${encodeURIComponent(feedKey)}`, {
    method: "DELETE"
  });

  if (!response.ok) {
    throw new Error(`Delete feed failed with status ${response.status}`);
  }
}

export type TenantMutationInput = {
  name: string;
  tier: string;
  ingestionLimitPerMinute: number;
  assignedLocationIds: string[];
};

export async function updateTenant(tenantId: string, input: Partial<TenantMutationInput>) {
  return requestJson<{ tenant: Tenant }>(`/api/admin/tenants/${encodeURIComponent(tenantId)}`, {
    method: "PUT",
    body: JSON.stringify(input)
  });
}

export async function provisionTenant(tenantId: string) {
  return requestJson<{ tenant: Tenant }>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}/provision`,
    { method: "POST" }
  );
}

export async function suspendTenant(tenantId: string) {
  return requestJson<{ tenant: Tenant }>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}/suspend`,
    { method: "POST" }
  );
}

export async function deleteTenant(tenantId: string) {
  const response = await apiFetch(`/api/admin/tenants/${encodeURIComponent(tenantId)}`, {
    method: "DELETE"
  });

  if (!response.ok) {
    throw new Error(`Delete tenant failed with status ${response.status}`);
  }
}

export async function clearFeedHistory(feedKey: string) {
  const response = await apiFetch(`/api/feeds/${encodeURIComponent(feedKey)}/history`, {
    method: "DELETE"
  });

  if (!response.ok) {
    throw new Error(`Clear history failed with status ${response.status}`);
  }
}
